import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { useDialog } from '../components/Dialog';
import StatusBadge from '../components/StatusBadge';
import {
  BackLink,
  ChatThread,
  Field,
  MapLink,
  Timeline,
  usePerson,
} from '../components/Detail';
import { adminRecomputePayBreakdown } from '../lib/api';
import { formatDateTime, money } from '../lib/format';

type Doc = Record<string, unknown>;

function loc(o: Doc, key: string) {
  const l = o[key] as Doc | undefined;
  return {
    address: typeof l?.address === 'string' ? l.address : '',
    lat: typeof l?.latitude === 'number' ? l.latitude : undefined,
    lng: typeof l?.longitude === 'number' ? l.longitude : undefined,
  };
}

const PAY_ROWS: { key: string; label: string; multiplier?: boolean }[] = [
  { key: 'baseFee', label: 'Base fee' },
  { key: 'distancePay', label: 'Distance pay' },
  { key: 'timePay', label: 'Time pay' },
  { key: 'longDistanceBonus', label: 'Long-distance bonus' },
  { key: 'heavyLoadBonus', label: 'Heavy-load bonus' },
  { key: 'urgentBonus', label: 'Urgent bonus' },
  { key: 'surgePay', label: 'Surge pay' },
  { key: 'peakMultiplier', label: 'Peak multiplier', multiplier: true },
];

export default function JobDetailPage() {
  const { id = '' } = useParams();
  // undefined = loading, null = not found
  const [job, setJob] = useState<Doc | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useDialog();

  useEffect(() => {
    if (!id) return;
    const unsub = onSnapshot(
      doc(db, 'jobs', id),
      (snap) =>
        setJob(snap.exists() ? { ...(snap.data() as Doc), id: snap.id } : null),
      (e) => setError(`Failed to load job: ${e.message}`)
    );
    return unsub;
  }, [id]);

  const driver = usePerson(
    (job?.driverId ?? job?.assignedDriverId) as string | undefined
  );

  const onRecompute = async () => {
    const ok = await confirm({
      title: 'Recompute pay breakdown',
      message: `Recompute pay breakdown for job ${id}? The new amount becomes payable on completion.`,
      confirmLabel: 'Recompute',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await adminRecomputePayBreakdown({ jobId: id });
      const b = (res.data.payBreakdown ?? {}) as Record<string, number>;
      const lines = Object.entries(b)
        .filter(([, v]) => typeof v === 'number' && v !== 0)
        .map(
          ([k, v]) =>
            `${k.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}: ${
              k === 'peakMultiplier' ? `${v}×` : money(v)
            }`
        )
        .join('\n');
      await confirm({
        title: 'Pay breakdown updated',
        message: lines || 'Breakdown recomputed.',
        confirmLabel: 'Done',
        cancelLabel: 'Close',
      });
    } catch (e) {
      setError(`Recompute failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  };

  if (job === undefined) {
    return (
      <>
        <BackLink to="/jobs" label="Back to jobs" />
        <p className="text-sm text-slate-500">Loading job…</p>
      </>
    );
  }
  if (job === null) {
    return (
      <>
        <BackLink to="/jobs" label="Back to jobs" />
        <Card>
          <p className="text-sm text-slate-600">
            Job <span className="font-mono">{id}</span> was not found.
          </p>
        </Card>
      </>
    );
  }

  const status = job.status as string | undefined;
  const pickup = loc(job, 'pickupLocation');
  const delivery = loc(job, 'deliveryLocation');
  const history = ((job.statusHistory as Doc[] | undefined) ?? []).map((h) => ({
    status: h.status as string | undefined,
    timestamp: h.timestamp,
    notes: h.notes as string | undefined,
    updatedBy: h.updatedBy as string | undefined,
  }));
  const pay = job.payBreakdown as Doc | undefined;
  const info = (job.customerInfo as Doc | undefined) ?? {};
  const orderId = job.orderId as string | undefined;
  const equipment = (job.equipmentRequired as string[] | undefined) ?? [];
  const customerName =
    (typeof info.companyName === 'string' && info.companyName) ||
    (typeof info.name === 'string' && info.name) ||
    'Job';

  return (
    <>
      <BackLink to="/jobs" label="Back to jobs" />
      <PageHeader title={customerName} subtitle={`Job ${id}`}>
        <button
          onClick={onRecompute}
          disabled={busy}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
        >
          Recompute pay
        </button>
      </PageHeader>
      {dialog}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Status & timeline">
          <div className="mb-3 flex items-center justify-between">
            <StatusBadge status={status} />
            {job.isUrgent === true && (
              <span className="rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20">
                Urgent
              </span>
            )}
          </div>
          <Field label="Created" value={formatDateTime(job.createdAt)} />
          <Field label="Accepted" value={formatDateTime(job.acceptedAt)} />
          <Field label="Completed" value={formatDateTime(job.completedAt)} />
          {job.scheduledPickupTime != null && (
            <Field
              label="Scheduled pickup"
              value={formatDateTime(job.scheduledPickupTime)}
            />
          )}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <Timeline entries={history} />
          </div>
        </Card>

        <Card title="Route">
          <Field
            label="Pickup"
            value={
              <MapLink lat={pickup.lat} lng={pickup.lng}>
                {pickup.address || '—'}
              </MapLink>
            }
          />
          <Field
            label="Drop-off"
            value={
              <MapLink lat={delivery.lat} lng={delivery.lng}>
                {delivery.address || '—'}
              </MapLink>
            }
          />
          <Field
            label="Distance"
            value={
              typeof job.distance === 'number'
                ? `${job.distance.toFixed(1)} mi`
                : undefined
            }
          />
          <Field
            label="Est. duration"
            value={
              typeof job.estimatedDuration === 'number' &&
              job.estimatedDuration > 0
                ? `${Math.round(job.estimatedDuration)} min`
                : undefined
            }
          />
        </Card>

        <Card title="Earnings">
          <Field
            label="Total earnings"
            value={money(job.totalEarnings as number)}
          />
          <Field label="Base pay" value={money(job.basePay as number)} />
          <Field label="Tip" value={money(job.tip as number)} />
          {pay && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              {PAY_ROWS.map(({ key, label, multiplier }) =>
                typeof pay[key] === 'number' &&
                (multiplier ? (pay[key] as number) !== 1 : true) ? (
                  <Field
                    key={key}
                    label={label}
                    value={
                      multiplier
                        ? `${pay[key] as number}×`
                        : money(pay[key] as number)
                    }
                  />
                ) : null
              )}
            </div>
          )}
        </Card>

        <Card title="Assignment & customer">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Driver
          </div>
          {job.driverId || job.assignedDriverId ? (
            <Field
              label="Name"
              value={
                <Link
                  to={`/drivers?q=${job.driverId ?? job.assignedDriverId}`}
                  className="text-brand-600 hover:underline"
                >
                  {driver?.name ?? '…'}
                </Link>
              }
            />
          ) : (
            <p className="py-1.5 text-sm text-slate-400">Not yet assigned.</p>
          )}
          {job.assignExpiresAt != null && (
            <Field
              label="Offer expires"
              value={formatDateTime(job.assignExpiresAt)}
            />
          )}
          <div className="mt-3 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Customer
          </div>
          <Field label="Name" value={info.name as string} />
          <Field label="Company" value={info.companyName as string} />
          <Field label="Phone" value={info.phone as string} />
          <Field label="Email" value={info.email as string} />
          {orderId && (
            <Field
              label="Order"
              value={
                <Link
                  to={`/orders/${orderId}`}
                  className="font-mono text-xs text-brand-600 hover:underline"
                >
                  {orderId}
                </Link>
              }
            />
          )}
        </Card>

        <Card title="Details">
          <Field
            label="Vehicle required"
            value={job.vehicleTypeRequired as string}
          />
          <Field
            label="Equipment"
            value={equipment.length ? equipment.join(', ') : undefined}
          />
          <Field
            label="Total weight"
            value={
              typeof job.totalWeight === 'number' && job.totalWeight > 0
                ? `${job.totalWeight} lb`
                : undefined
            }
          />
          <Field
            label="Instructions"
            value={job.specialInstructions as string}
          />
        </Card>

        <Card title="Messages">
          <ChatThread orderId={orderId ?? ''} />
        </Card>
      </div>
    </>
  );
}
